import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Observable, throwError } from 'rxjs';

import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import {
  Game,
  GameStatus,
  SeasonType,
  StathletesExportRequest,
  StathletesSeasonExport,
  StathletesSeasonFilters,
  StathletesSeasonPreview,
  Video
} from '../domain/game';
import { ValidationCheck } from '../domain/validation-check';
import { GamePeriod, periodKeyCodeMap } from '../domain/game-event';
import { QualityCheck } from '../game-quality-dialog/game-quality-dialog.component';
import { DatePipe } from '@angular/common';
import { Player } from '../domain/player';

export interface VideoDownloadOptions {
  fullLength: boolean;
  clipped: boolean;
  clipStart?: number;
  clipEnd?: number;
  hls?: boolean;
}

export interface TimeOnIceReport {
  qualityChecks: QualityCheck[];
  total: QualityCheck;
}

export interface BulkImportError {
  row: number;
  fields: string[];
  message: string;
}

export interface BulkImportResult {
  message: string;
  insertedCount: number;
}

@Injectable()
export class GameService {
  private gamesUrl = environment.API_HOST + '/api/games';
  private baseUrl = environment.API_HOST + '/api';

  constructor(private http: HttpClient, private datePipe: DatePipe) {}

  /**
   * Returns total game count and games for the requested page
   */
  findGames(
    filters: any,
    page: number,
    pageSize: number,
    sortField: string,
    sortDirection: SortDirection
  ): Observable<[number, Game[]]> {
    const definedFilters = Object.keys(filters)
      .filter((k) => filters[k] != null)
      .reduce((a, k) => ({ ...a, [k]: filters[k] }), {});
    const params = new HttpParams({ fromObject: definedFilters })
      .set('page', page)
      .set('pageSize', pageSize)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection)
      .set('timezoneOffset', new Date().getTimezoneOffset());
    return this.http.get<any>(this.gamesUrl, { params }).pipe(
      map((json) => {
        const count: number = json['count'];
        const games: Game[] = json['data'].map((obj) => new Game(obj));
        return [count, games] as [number, Game[]];
      }),
      catchError(this.handleError)
    );
  }

  getGame(id: string): Observable<Game> {
    const url = `${this.gamesUrl}/${id}`;
    return this.http.get(url).pipe(
      map((obj) => new Game(obj)),
      catchError(this.handleError)
    );
  }

  private handleError(httpErrorResponse: Response | any) {
    let errMsg: string;
    if (httpErrorResponse.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errMsg = httpErrorResponse.message
        ? httpErrorResponse.message
        : httpErrorResponse.toString();
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      if (httpErrorResponse.status === 422) {
        // validation failed
        return throwError(() => new Error(httpErrorResponse.error.message));
      } else {
        errMsg = `${httpErrorResponse.status} - ${
          httpErrorResponse.statusText || ''
        } ${httpErrorResponse?.error?.message ? ': ' + httpErrorResponse?.error?.message : ''}`;
        console.error(`Backend returned error: ${errMsg}`);
      }
    }
    return throwError(() => new Error(errMsg));
  }

  create(game: Game): Observable<any> {
    return this.http
      .post(this.gamesUrl, game)
      .pipe(catchError(this.handleError));
  }

  update(
    _id: string,
    changes: Partial<Game> & { confirmTrainingDcDeletion?: boolean }
  ): Observable<any> {
    return this.http.patch(this.gamesUrl, { _id, ...changes }).pipe(
      catchError((error) => {
        if (
          error.status === 409 &&
          error.error?.code === 'TRAINING_GAME_CONFIRMATION_REQUIRED'
        ) {
          return throwError(() => error);
        }
        return this.handleError(error);
      })
    );
  }

  delete(game: Game) {
    return this.http
      .delete(this.gamesUrl + '/' + game._id)
      .pipe(catchError(this.handleError));
  }

  duplicateGame(gameId: string, duplicateEventTypes: string[]) {
    return this.http.post<Game>(
      `${this.gamesUrl}/${gameId}/duplicate`,
      duplicateEventTypes
    );
  }

  getGameShiftsAsCSV(gameId: string): Observable<string> {
    return this.http
      .get(`${this.gamesUrl}/export-shifts-csv/${gameId}`, {
        responseType: 'text'
      })
      .pipe(catchError(this.handleError));
  }

  getGameEventsAsCSV(game: Game): Observable<string> {
    return this.http
      .get(`${this.gamesUrl}/export-csv/${game._id}`, { responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  getGameEventsAsXML(
    game: Game,
    teamId: string,
    timeOnIce: boolean,
    gameActions: boolean
  ): Observable<string> {
    return this.http
      .get(
        `${this.gamesUrl}/${game._id}/export-xml?teamId=${teamId}&time-on-ice=${timeOnIce}&game-actions=${gameActions}`,
        { responseType: 'text' }
      )
      .pipe(catchError(this.handleError));
  }

  importGameEventsAsCSV(game: Game, data: string): Observable<any> {
    return this.http
      .post(`${this.gamesUrl}/import-csv/${game._id}`, { data })
      .pipe(catchError(this.handleError));
  }

  bulkImportGames(file: File): Observable<BulkImportResult> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<BulkImportResult>(`${this.gamesUrl}/bulk-import`, formData)
      .pipe(
        catchError((error) => {
          if (error?.status === 422) {
            return throwError(() => error);
          }
          return this.handleError(error);
        })
      );
  }

  publishFullGame(
    game: Game,
    teamId: string,
    timeOnIce: boolean,
    gameActions: boolean
  ): Observable<void> {
    return this.http.post<any>(
      `${this.gamesUrl}/${game._id}/publish-sihf?time-on-ice=${timeOnIce}&game-actions=${gameActions}`,
      { teamId, timeOnIce, gameActions }
    );
  }

  getAllPlayers(game: Game): string[] {
    return this.getHomeTeamPlayers(game)
      .concat(this.getAwayTeamPlayers(game))
      .filter(function (n) {
        return n !== undefined;
      });
  }

  getPlayers(game: Game, team: string) {
    const isHomeTeam = team === game.homeTeam;
    return isHomeTeam
      ? this.getHomeTeamPlayers(game)
      : this.getAwayTeamPlayers(game);
  }

  getAwayTeamPlayers(game: Game): string[] {
    let awayTeamPlayers = [game.awayGoalkeeper, game.awayBackupGoalkeeper];
    awayTeamPlayers = awayTeamPlayers.concat(this.getAwayTeamDefensemen(game));
    awayTeamPlayers = awayTeamPlayers.concat(this.getAwayTeamForwards(game));
    return awayTeamPlayers.filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getAwayTeamDefensemen(game: Game) {
    return [
      game.awayFirstLineLeftDefence,
      game.awayFirstLineRightDefence,
      game.awaySecondLineLeftDefence,
      game.awaySecondLineRightDefence,
      game.awayThirdLineLeftDefence,
      game.awayThirdLineRightDefence,
      game.awayFourthLineLeftDefence,
      game.awayFourthLineRightDefence,
      game.awayFifthLineLeftDefence,
      game.awayFifthLineRightDefence,
      game.awaySixthLineLeftDefence,
      game.awaySixthLineRightDefence,
      game.awaySeventhLineLeftDefence,
      game.awaySeventhLineRightDefence,
      game.awayEighthLineLeftDefence,
      game.awayEighthLineRightDefence,
      game.awayExtraLineDefence
    ].filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getAwayTeamForwards(game: Game) {
    return [
      game.awayFirstLineLeftForward,
      game.awayFirstLineCenter,
      game.awayFirstLineRightForward,
      game.awaySecondLineLeftForward,
      game.awaySecondLineCenter,
      game.awaySecondLineRightForward,
      game.awayThirdLineLeftForward,
      game.awayThirdLineCenter,
      game.awayThirdLineRightForward,
      game.awayFourthLineLeftForward,
      game.awayFourthLineCenter,
      game.awayFourthLineRightForward,
      game.awayFifthLineLeftForward,
      game.awayFifthLineCenter,
      game.awayFifthLineRightForward,
      game.awaySixthLineLeftForward,
      game.awaySixthLineCenter,
      game.awaySixthLineRightForward,
      game.awaySeventhLineLeftForward,
      game.awaySeventhLineCenter,
      game.awaySeventhLineRightForward,
      game.awayEighthLineLeftForward,
      game.awayEighthLineCenter,
      game.awayEighthLineRightForward,
      game.awayExtraLineForward
    ].filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getHomeTeamPlayers(game: Game): string[] {
    let homeTeamPlayers = [game.homeGoalkeeper, game.homeBackupGoalkeeper];
    homeTeamPlayers = homeTeamPlayers.concat(this.getHomeTeamDefensemen(game));
    homeTeamPlayers = homeTeamPlayers.concat(this.getHomeTeamForwards(game));
    return homeTeamPlayers.filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getHomeTeamDefensemen(game: Game) {
    return [
      game.homeFirstLineLeftDefence,
      game.homeFirstLineRightDefence,
      game.homeSecondLineLeftDefence,
      game.homeSecondLineRightDefence,
      game.homeThirdLineLeftDefence,
      game.homeThirdLineRightDefence,
      game.homeFourthLineLeftDefence,
      game.homeFourthLineRightDefence,
      game.homeFifthLineLeftDefence,
      game.homeFifthLineRightDefence,
      game.homeSixthLineLeftDefence,
      game.homeSixthLineRightDefence,
      game.homeSeventhLineLeftDefence,
      game.homeSeventhLineRightDefence,
      game.homeEighthLineLeftDefence,
      game.homeEighthLineRightDefence,
      game.homeExtraLineDefence,
      game.homeExtraLineForward
    ].filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getHomeTeamForwards(game: Game) {
    return [
      game.homeFirstLineLeftForward,
      game.homeFirstLineCenter,
      game.homeFirstLineRightForward,
      game.homeSecondLineLeftForward,
      game.homeSecondLineCenter,
      game.homeSecondLineRightForward,
      game.homeThirdLineLeftForward,
      game.homeThirdLineCenter,
      game.homeThirdLineRightForward,
      game.homeFourthLineLeftForward,
      game.homeFourthLineCenter,
      game.homeFourthLineRightForward,
      game.homeFifthLineLeftForward,
      game.homeFifthLineCenter,
      game.homeFifthLineRightForward,
      game.homeSixthLineLeftForward,
      game.homeSixthLineCenter,
      game.homeSixthLineRightForward,
      game.homeSeventhLineLeftForward,
      game.homeSeventhLineCenter,
      game.homeSeventhLineRightForward,
      game.homeEighthLineLeftForward,
      game.homeEighthLineCenter,
      game.homeEighthLineRightForward,
      game.homeExtraLineDefence,
      game.homeExtraLineForward
    ].filter(
      (player) => player != null && player !== 'n/a' && player.trim() !== ''
    );
  }

  getHomeTeamPlayerByJerseyNumber(game: Game, jerseyNumber: string) {
    return this.getHomeTeamPlayers(game).filter(
      (player) => this.getJerseyNumberFromPlayer(player) === jerseyNumber
    )[0];
  }

  getAwayTeamPlayerByJerseyNumber(game: Game, jerseyNumber: string) {
    return this.getAwayTeamPlayers(game).filter(
      (player) => this.getJerseyNumberFromPlayer(player) === jerseyNumber
    )[0];
  }

  findPlayerByJerseyNumber(game: Game, team: string, jerseyNumber: string) {
    const players = this.getPlayers(game, team);
    return players.find(
      (player) => this.getJerseyNumberFromPlayer(player) === jerseyNumber
    );
  }

  getPlayerIdByJerseyNumber(
    game: Game,
    isHomeTeam: boolean,
    jerseyNumber: string
  ): string {
    const players = isHomeTeam
      ? this.getHomeTeamPlayersById(game)
      : this.getAwayTeamPlayersById(game);
    const result = players
      .filter((t) => t[0] != null && t[1] != null)
      .filter((player) => player[1] === jerseyNumber);
    if (result.length === 1) {
      return result[0][0];
    } else {
      return null;
    }
  }

  private getHomeTeamPlayersById(game: Game): [string, string][] {
    return [
      [game.homeGoalkeeperID, game.homeGoalkeeper],
      [game.homeBackupGoalkeeperID, game.homeBackupGoalkeeper],
      [game.homeFirstLineLeftDefenceID, game.homeFirstLineLeftDefence],
      [game.homeFirstLineRightDefenceID, game.homeFirstLineRightDefence],
      [game.homeSecondLineLeftDefenceID, game.homeSecondLineLeftDefence],
      [game.homeSecondLineRightDefenceID, game.homeSecondLineRightDefence],
      [game.homeThirdLineLeftDefenceID, game.homeThirdLineLeftDefence],
      [game.homeThirdLineRightDefenceID, game.homeThirdLineRightDefence],
      [game.homeFourthLineLeftDefenceID, game.homeFourthLineLeftDefence],
      [game.homeFourthLineRightDefenceID, game.homeFourthLineRightDefence],
      [game.homeFifthLineLeftDefenceID, game.homeFifthLineLeftDefence],
      [game.homeFifthLineRightDefenceID, game.homeFifthLineRightDefence],
      [game.homeSixthLineLeftDefenceID, game.homeSixthLineLeftDefence],
      [game.homeSixthLineRightDefenceID, game.homeSixthLineRightDefence],
      [game.homeSeventhLineLeftDefenceID, game.homeSeventhLineLeftDefence],
      [game.homeSeventhLineRightDefenceID, game.homeSeventhLineRightDefence],
      [game.homeEighthLineLeftDefenceID, game.homeEighthLineLeftDefence],
      [game.homeEighthLineRightDefenceID, game.homeEighthLineRightDefence],
      [game.homeExtraLineDefenceID, game.homeExtraLineDefence],

      [game.homeFirstLineLeftForwardID, game.homeFirstLineLeftForward],
      [game.homeFirstLineCenterID, game.homeFirstLineCenter],
      [game.homeFirstLineRightForwardID, game.homeFirstLineRightForward],
      [game.homeSecondLineLeftForwardID, game.homeSecondLineLeftForward],
      [game.homeSecondLineCenterID, game.homeSecondLineCenter],
      [game.homeSecondLineRightForwardID, game.homeSecondLineRightForward],
      [game.homeThirdLineLeftForwardID, game.homeThirdLineLeftForward],
      [game.homeThirdLineCenterID, game.homeThirdLineCenter],
      [game.homeThirdLineRightForwardID, game.homeThirdLineRightForward],
      [game.homeFourthLineLeftForwardID, game.homeFourthLineLeftForward],
      [game.homeFourthLineCenterID, game.homeFourthLineCenter],
      [game.homeFourthLineRightForwardID, game.homeFourthLineRightForward],
      [game.homeFifthLineLeftForwardID, game.homeFifthLineLeftForward],
      [game.homeFifthLineCenterID, game.homeFifthLineCenter],
      [game.homeFifthLineRightForwardID, game.homeFifthLineRightForward],
      [game.homeSixthLineLeftForwardID, game.homeSixthLineLeftForward],
      [game.homeSixthLineCenterID, game.homeSixthLineCenter],
      [game.homeSixthLineRightForwardID, game.homeSixthLineRightForward],
      [game.homeSeventhLineLeftForwardID, game.homeSeventhLineLeftForward],
      [game.homeSeventhLineCenterID, game.homeSeventhLineCenter],
      [game.homeSeventhLineRightForwardID, game.homeSeventhLineRightForward],
      [game.homeEighthLineLeftForwardID, game.homeEighthLineLeftForward],
      [game.homeEighthLineCenterID, game.homeEighthLineCenter],
      [game.homeEighthLineRightForwardID, game.homeEighthLineRightForward],
      [game.homeExtraLineForwardID, game.homeExtraLineForward]
    ];
  }

  private getAwayTeamPlayersById(game: Game): [string, string][] {
    return [
      [game.awayGoalkeeperID, game.awayGoalkeeper],
      [game.awayBackupGoalkeeperID, game.awayBackupGoalkeeper],
      [game.awayFirstLineLeftDefenceID, game.awayFirstLineLeftDefence],
      [game.awayFirstLineRightDefenceID, game.awayFirstLineRightDefence],
      [game.awaySecondLineLeftDefenceID, game.awaySecondLineLeftDefence],
      [game.awaySecondLineRightDefenceID, game.awaySecondLineRightDefence],
      [game.awayThirdLineLeftDefenceID, game.awayThirdLineLeftDefence],
      [game.awayThirdLineRightDefenceID, game.awayThirdLineRightDefence],
      [game.awayFourthLineLeftDefenceID, game.awayFourthLineLeftDefence],
      [game.awayFourthLineRightDefenceID, game.awayFourthLineRightDefence],
      [game.awayFifthLineLeftDefenceID, game.awayFifthLineLeftDefence],
      [game.awayFifthLineRightDefenceID, game.awayFifthLineRightDefence],
      [game.awaySixthLineLeftDefenceID, game.awaySixthLineLeftDefence],
      [game.awaySixthLineRightDefenceID, game.awaySixthLineRightDefence],
      [game.awaySeventhLineLeftDefenceID, game.awaySeventhLineLeftDefence],
      [game.awaySeventhLineRightDefenceID, game.awaySeventhLineRightDefence],
      [game.awayEighthLineLeftDefenceID, game.awayEighthLineLeftDefence],
      [game.awayEighthLineRightDefenceID, game.awayEighthLineRightDefence],
      [game.awayExtraLineDefenceID, game.awayExtraLineDefence],

      [game.awayFirstLineLeftForwardID, game.awayFirstLineLeftForward],
      [game.awayFirstLineCenterID, game.awayFirstLineCenter],
      [game.awayFirstLineRightForwardID, game.awayFirstLineRightForward],
      [game.awaySecondLineLeftForwardID, game.awaySecondLineLeftForward],
      [game.awaySecondLineCenterID, game.awaySecondLineCenter],
      [game.awaySecondLineRightForwardID, game.awaySecondLineRightForward],
      [game.awayThirdLineLeftForwardID, game.awayThirdLineLeftForward],
      [game.awayThirdLineCenterID, game.awayThirdLineCenter],
      [game.awayThirdLineRightForwardID, game.awayThirdLineRightForward],
      [game.awayFourthLineLeftForwardID, game.awayFourthLineLeftForward],
      [game.awayFourthLineCenterID, game.awayFourthLineCenter],
      [game.awayFourthLineRightForwardID, game.awayFourthLineRightForward],
      [game.awayFifthLineLeftForwardID, game.awayFifthLineLeftForward],
      [game.awayFifthLineCenterID, game.awayFifthLineCenter],
      [game.awayFifthLineRightForwardID, game.awayFifthLineRightForward],
      [game.awaySixthLineLeftForwardID, game.awaySixthLineLeftForward],
      [game.awaySixthLineCenterID, game.awaySixthLineCenter],
      [game.awaySixthLineRightForwardID, game.awaySixthLineRightForward],
      [game.awaySeventhLineLeftForwardID, game.awaySeventhLineLeftForward],
      [game.awaySeventhLineCenterID, game.awaySeventhLineCenter],
      [game.awaySeventhLineRightForwardID, game.awaySeventhLineRightForward],
      [game.awayEighthLineLeftForwardID, game.awayEighthLineLeftForward],
      [game.awayEighthLineCenterID, game.awayEighthLineCenter],
      [game.awayEighthLineRightForwardID, game.awayEighthLineRightForward],
      [game.awayExtraLineForwardID, game.awayExtraLineForward]
    ];
  }

  getJerseyNumberFromPlayer(player: string) {
    return player.split('-')[0].trim();
  }

  getNameFromPlayer(player: string) {
    return player.split('-')[1].trim();
  }

  getTeamNames(game: Game) {
    return [game.homeTeam, game.awayTeam];
  }

  downloadVideo(
    gameId: string,
    video: Video,
    downloadOptions: VideoDownloadOptions
  ) {
    return this.http.post<{ status: string }>(
      `${this.gamesUrl}/${gameId}/download-video`,
      {
        video,
        downloadOptions
      }
    );
  }

  syncSeason(season, league): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/games/sync-completed?season=${season}&league=${league}`,
      {}
    );
  }

  backfillSeasonEventIds(season, league, apply): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/games/backfill-event-ids?season=${season}&league=${league}&apply=${apply}`,
      {}
    );
  }

  refreshCVCandidacy(season, league, apply): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/games/refresh-cv-candidacy?season=${season}&league=${league}&apply=${apply}`,
      {}
    );
  }

  backfillGameEventIds(gameId: string, apply): Observable<any> {
    return this.http.post(
      `${this.gamesUrl}/${gameId}/backfill-event-ids?apply=${apply}`,
      {}
    );
  }

  syncGame(gameId: string, period: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/games/sync-game`, {
      gameId,
      period
    });
  }

  validateGame(gameId: string): Observable<ValidationCheck[]> {
    return this.http.post<ValidationCheck[]>(
      `${this.baseUrl}/games/${gameId}/validate`,
      {}
    );
  }

  updateGameValidations(game: Game, validations: ValidationCheck[]) {
    game.hasErrors = validations.some((v) => v.status === 'error');
    game.hasWarnings = validations.some((v) => v.status === 'warning');
  }

  timeOnIceReport(gameId: string) {
    return this.http.get<TimeOnIceReport>(
      `${this.baseUrl}/games/${gameId}/time-on-ice-report`
    );
  }

  updateStatus(
    gameId: string,
    status: GameStatus
  ): Observable<ValidationCheck[]> {
    return this.http.put<ValidationCheck[]>(
      `${this.baseUrl}/games/${gameId}/status`,
      {
        status
      }
    );
  }

  allTags() {
    return this.http.get<string[]>(`${this.baseUrl}/games/tags`);
  }

  adjustLineup(
    gameId: string,
    existingPlayerNumber: string,
    existingPlayerId: string,
    newPlayerNumber: string,
    newPlayerId: string
  ) {
    return this.http.post(`${this.baseUrl}/games/adjust-lineup`, {
      gameId,
      existingPlayerNumber,
      existingPlayerId,
      newPlayerNumber,
      newPlayerId
    });
  }

  getTeamByPlayerName(game: Game, player: string): string {
    if (this.getHomeTeamPlayers(game).indexOf(player) > -1) {
      return game.homeTeam;
    } else if (this.getAwayTeamPlayers(game).indexOf(player) > -1) {
      return game.awayTeam;
    } else {
      return null;
    }
  }

  runCVPipeline(
    gameId: string,
    liveMode: boolean,
    periods: string,
    phases: string,
    priority: 'low' | 'high'
  ): Observable<void> {
    return this.http.post<any>(`${this.gamesUrl}/${gameId}/run-cv-pipeline`, {
      liveMode,
      periods,
      phases,
      priority
    });
  }

  runSeasonAggregation(
    season: number,
    league: string,
    seasonType: SeasonType
  ): Observable<{ message: string; aggregationTaskArn: string }> {
    return this.http.post<{ message: string; aggregationTaskArn: string }>(
      `${this.gamesUrl}/run-season-aggregation`,
      {},
      { params: { season, league, seasonType } }
    );
  }

  runAggregation(gameId: string): Observable<any> {
    return this.http
      .post<{ message: string; taskId?: string; error?: string }>(
        `${this.gamesUrl}/${gameId}/run-aggregation`,
        {}
      )
      .pipe(
        map((res) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res;
        })
      );
  }

  importGoalsFromSihfReporter(
    gameId: string,
    options: { scorer: boolean; assists: boolean }
  ): Observable<any> {
    return this.http
      .post<any>(`${this.gamesUrl}/${gameId}/import-goals`, options)
      .pipe(
        map((res) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res;
        })
      );
  }

  recalculatePoints(
    gameId: string,
    params: {
      homeGoals: number | null;
      awayGoals: number | null;
      overtime: boolean;
      shootout: boolean;
    }
  ): Observable<{ homePoints: number; awayPoints: number }> {
    return this.http.post<{ homePoints: number; awayPoints: number }>(
      `${this.gamesUrl}/${gameId}/recalculate-points`,
      params
    );
  }

  recomputeScore(
    gameId: string
  ): Observable<{ homeGoals: number; awayGoals: number }> {
    return this.http.post<{ homeGoals: number; awayGoals: number }>(
      `${this.gamesUrl}/${gameId}/recompute-score`,
      {}
    );
  }

  importPenaltiesFromSihfReporter(gameId: string): Observable<any> {
    return this.http
      .post<any>(`${this.gamesUrl}/${gameId}/import-penalties`, {})
      .pipe(
        map((res) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res;
        })
      );
  }

  applyScheduleCorrection(gameId: string): Observable<Game> {
    return this.http
      .post<Game>(`${this.gamesUrl}/${gameId}/apply-schedule-correction`, {})
      .pipe(
        map((obj) => new Game(obj)),
        catchError(this.handleError)
      );
  }

  syncGameIncidents(gameId: string): Observable<any> {
    return this.http
      .post<any>(`${this.gamesUrl}/${gameId}/sync-game-incidents`, {})
      .pipe(
        map((res) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res;
        })
      );
  }

  exportStathletes(gameId: string): Observable<StathletesExportRequest> {
    return this.http.post<StathletesExportRequest>(
      `${this.gamesUrl}/${gameId}/export-stathletes`,
      {}
    );
  }

  previewStathletesSeason(
    season: number | string,
    league: string,
    filters: StathletesSeasonFilters = {}
  ): Observable<StathletesSeasonPreview> {
    return this.http.post<StathletesSeasonPreview>(
      `${this.gamesUrl}/export-stathletes`,
      {},
      { params: this.stathletesSeasonParams(season, league, filters) }
    );
  }

  exportStathletesSeason(
    season: number | string,
    league: string,
    filters: StathletesSeasonFilters,
    gameIds: string[]
  ): Observable<StathletesSeasonExport> {
    // the server exports only these, the games the user confirmed
    return this.http.post<StathletesSeasonExport>(
      `${this.gamesUrl}/export-stathletes`,
      { gameIds },
      {
        params: {
          ...this.stathletesSeasonParams(season, league, filters),
          apply: 'true'
        }
      }
    );
  }

  private stathletesSeasonParams(
    season: number | string,
    league: string,
    { seasonType, undeliveredOnly }: StathletesSeasonFilters
  ) {
    return {
      season,
      league,
      ...(seasonType ? { seasonType } : {}),
      ...(undeliveredOnly ? { undeliveredOnly: 'true' } : {})
    };
  }

  removePlayersWithoutEvents(
    gameId: string,
    teams: Array<'home' | 'away'>
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.gamesUrl}/${gameId}/remove-players-without-events`,
      { teams }
    );
  }

  findPeriodByKeyCode(keyCode: number): GamePeriod {
    return periodKeyCodeMap[keyCode];
  }

  getGameTitle(game: Game) {
    return `${this.datePipe.transform(game.date, 'yyyy-MM-dd')} ${
      game.homeTeam
    } - ${game.awayTeam}`;
  }

  swapLineupPlayers(game: Game, sourcePlayer: Player, targetPlayer: Player) {
    return this.http
      .post<any>(`${this.gamesUrl}/${game._id}/swap-lineup-players`, {
        sourcePlayer,
        targetPlayer
      })
      .pipe(
        map((res) => {
          if (res.error) {
            throw new Error(res.error);
          }
          return res;
        }),
        catchError(this.handleError)
      );
  }

  lockSwapLineupPlayers(gameId: string, lockSwapLineupPlayers: boolean) {
    return this.http
      .post<{ message: string; lockSwapLineupPlayers: boolean }>(
        `${this.gamesUrl}/${gameId}/lock-swap-lineup-players`,
        { lockSwapLineupPlayers }
      )
      .pipe(catchError(this.handleError));
  }
}
